import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { Globe, Buildings, MapPin, MagnifyingGlass, ArrowLeft, Plus, Lightbulb, ArrowRight } from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import TalkVibes from "@/components/TalkVibes";
import { isIndia } from "@/lib/edition";

export default function CountryDetail() {
  const { country } = useParams();
  const name = decodeURIComponent(country);
  const [params, setParams] = useSearchParams();
  useSeo(name, `Explore schools, colleges and universities in ${name} by state — search and suggest institutions on AlumniPulse.`);

  const [states, setStates] = useState([]);       // [{state, count, cities}]
  const [total, setTotal] = useState(0);
  const [active, setActive] = useState(params.get("state") || "all"); // "all" | stateName | "__suggest__"
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);

  // Load the state facets for this country
  useEffect(() => {
    api.get("/institutions/locations").then((r) => {
      const node = (r.data || []).find((c) => c.country === name);
      if (node) {
        setStates(node.states.filter((s) => s.state !== "—"));
        setTotal(node.count);
      }
    }).catch(() => {});
  }, [name]);

  const load = useCallback(() => {
    if (active === "__suggest__") return;
    setItems(null);
    const p = { country: name, q, limit: 200 };
    if (active !== "all") p.state = active;
    api.get("/institutions", { params: p }).then((r) => setItems(r.data.results || [])).catch(() => setItems([]));
  }, [name, active, q]);
  useEffect(() => { load(); }, [load]);

  const selectTab = (v) => {
    setActive(v);
    setQ("");
    const np = new URLSearchParams(params);
    if (v === "all" || v === "__suggest__") np.delete("state"); else np.set("state", v);
    setParams(np, { replace: true });
  };

  const suggestLink = `/institutions/suggest?country=${encodeURIComponent(name)}${active !== "all" && active !== "__suggest__" ? `&state=${encodeURIComponent(active)}` : ""}`;

  return (
    <div>
      {/* Hero */}
      <section className="border-b border-border/60 bg-gradient-to-br from-accent/40 to-background">
        <div className="mx-auto max-w-7xl px-5 py-10">
          {!isIndia && <Link to="/countries" className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft size={16} /> All countries</Link>}
          <div className="flex items-center gap-3">
            <span className="grid h-12 w-12 place-items-center rounded-2xl bg-primary text-primary-foreground"><Globe size={26} weight="duotone" /></span>
            <div>
              <h1 className="font-heading text-4xl font-light tracking-tight" data-testid="country-name">{name}</h1>
              <p className="text-sm text-muted-foreground">{total} institution{total === 1 ? "" : "s"} across {states.length} state{states.length === 1 ? "" : "s"}/region{states.length === 1 ? "" : "s"}</p>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-5 py-8">
        {/* State tabs + Suggest tab */}
        <div className="flex gap-2 overflow-x-auto pb-2" data-testid="country-state-tabs">
          <button onClick={() => selectTab("all")} data-testid="country-tab-all"
            className={`shrink-0 rounded-full border px-4 py-1.5 text-sm transition-colors ${active === "all" ? "border-primary bg-primary text-primary-foreground" : "border-border/60 bg-card hover:border-primary/40"}`}>
            All states <span className="ml-1 opacity-70">{total}</span>
          </button>
          {states.map((s) => (
            <button key={s.state} onClick={() => selectTab(s.state)} data-testid={`country-tab-${s.state.replace(/\s+/g, "-").toLowerCase()}`}
              className={`shrink-0 rounded-full border px-4 py-1.5 text-sm transition-colors ${active === s.state ? "border-primary bg-primary text-primary-foreground" : "border-border/60 bg-card hover:border-primary/40"}`}>
              {s.state} <span className="ml-1 opacity-70">{s.count}</span>
            </button>
          ))}
          <button onClick={() => selectTab("__suggest__")} data-testid="country-tab-suggest"
            className={`shrink-0 rounded-full border px-4 py-1.5 text-sm transition-colors ${active === "__suggest__" ? "border-secondary bg-secondary text-secondary-foreground" : "border-secondary/50 bg-secondary/5 text-secondary hover:bg-secondary/10"}`}>
            <Lightbulb size={14} weight="fill" className="mr-1 inline" /> Suggest an institution
          </button>
        </div>

        {active === "__suggest__" ? (
          <div className="mt-8 rounded-3xl border border-secondary/25 bg-accent/30 p-10 text-center" data-testid="country-suggest-panel">
            <Lightbulb size={40} weight="duotone" className="mx-auto text-secondary" />
            <h2 className="mt-3 font-heading text-2xl font-light tracking-tight">Know a school or college in {name} we're missing?</h2>
            <p className="mx-auto mt-2 max-w-lg text-muted-foreground">Anyone can help — no account needed. A name and a location are enough to get us started; we'll research the rest and build its profile.</p>
            <Link to={suggestLink}><Button size="lg" className="mt-6 rounded-full gap-2" data-testid="country-suggest-cta">Suggest an institution <ArrowRight size={16} /></Button></Link>
          </div>
        ) : (
          <>
            {/* Search box on top */}
            <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-heading text-xl font-medium tracking-tight">
                {active === "all" ? `Institutions in ${name}` : `${active}`}
              </h2>
              <div className="relative w-full max-w-xs">
                <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${active === "all" ? name : active}…`} className="rounded-full pl-10" data-testid="country-search-box" />
              </div>
            </div>

            {items === null ? <p className="mt-8 text-muted-foreground">Loading…</p>
              : items.length === 0 ? (
                <div className="mt-8 rounded-2xl border border-dashed border-border/60 p-8 text-center">
                  <p className="text-muted-foreground">No institutions found{q ? ` for "${q}"` : ""}.</p>
                  <Link to={suggestLink}><Button variant="outline" className="mt-4 rounded-full gap-1.5" data-testid="country-empty-suggest"><Plus size={16} /> Suggest one</Button></Link>
                </div>
              ) : (
                <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="country-inst-grid">
                  {items.map((i) => (
                    <Link key={i.id} to={`/institutions/${i.id}`} data-testid={`country-inst-${i.id}`}
                      className="rounded-2xl border border-border/60 bg-card p-5 shadow-sm transition-colors hover:border-secondary/40">
                      <Badge variant="secondary" className="rounded-full font-normal">{i.type}</Badge>
                      <h3 className="mt-2 font-heading text-lg font-medium leading-tight">{i.name}</h3>
                      {i.location && <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground"><MapPin size={14} /> {i.location}</p>}
                    </Link>
                  ))}
                </div>
              )}
          </>
        )}
      </div>
      <div className="mx-auto max-w-5xl px-5 pb-12">
        {(() => {
          const isState = active !== "all" && active !== "__suggest__";
          return (
            <TalkVibes
              entityType={isState ? "state" : "country"}
              entityId={isState ? `${name}:${active}` : name}
              title={isState ? `${active} Education` : `${name} Education`}
              subtitle="Local education talk — institutions, alumni events, old schools & reunions."
              accent="geography" />
          );
        })()}
      </div>
    </div>
  );
}
